/**
 * URL Encoding/Decoding Utility
 *
 * Handles encoding and decoding of user data for passing between windows
 * Uses Base64 encoding for URL-safe transmission
 */

export interface ConsultationUserData {
  email: string;
  country: string;
  firstName?: string;
  lastName?: string;
}

/**
 * Encodes user data to a URL-safe base64 string
 * @param email User's email address
 * @param country User's country
 * @param firstName Optional first name
 * @param lastName Optional last name
 * @returns Base64-encoded string safe for URL parameters
 */
export function encodeUserData(
  email: string,
  country: string,
  firstName?: string,
  lastName?: string
): string {
  const data: ConsultationUserData = {
    email,
    country,
    ...(firstName && { firstName }),
    ...(lastName && { lastName })
  };

  try {
    const jsonString = JSON.stringify(data);
    // Use btoa for base64 encoding
    const base64 = btoa(jsonString);
    // Make URL-safe by replacing characters
    const urlSafe = base64
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');

    console.log('[urlEncoding] Encoded user data:', {
      email: email.substring(0, 3) + '***',  // Log partial for privacy
      country,
      encoded: urlSafe.substring(0, 20) + '...'
    });

    return urlSafe;
  } catch (error) {
    console.error('[urlEncoding] Failed to encode user data:', error);
    throw new Error('Failed to encode user data');
  }
}

/**
 * Decodes URL parameter back to user data object
 * @param encodedData Base64-encoded string from URL
 * @returns Decoded user data object
 * @throws Error if decoding fails
 */
export function decodeUserData(encodedData: string): ConsultationUserData {
  try {
    // Convert URL-safe back to standard base64
    let base64 = encodedData
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    // Add padding if needed
    while (base64.length % 4) {
      base64 += '=';
    }

    // Decode from base64
    const jsonString = atob(base64);
    const data = JSON.parse(jsonString) as ConsultationUserData;

    // Validate required fields
    if (!data.email || !data.country) {
      throw new Error('Invalid user data: missing email or country');
    }

    console.log('[urlEncoding] Decoded user data:', {
      email: data.email.substring(0, 3) + '***',  // Log partial for privacy
      country: data.country,
      hasFirstName: !!data.firstName,
      hasLastName: !!data.lastName
    });

    return data;
  } catch (error) {
    console.error('[urlEncoding] Failed to decode user data:', error);
    throw new Error('Failed to decode user data');
  }
}

/**
 * Builds a consultation URL with encoded user data
 * @param baseUrl Base URL (usually window.location.origin)
 * @param email User's email
 * @param country User's country
 * @param firstName Optional first name
 * @param lastName Optional last name
 * @returns Complete URL with encoded consultation parameter
 */
export function buildConsultationUrl(
  baseUrl: string,
  email: string,
  country: string,
  firstName?: string,
  lastName?: string
): string {
  const encoded = encodeUserData(email, country, firstName, lastName);
  const separator = baseUrl.includes('?') ? '&' : '?';
  return `${baseUrl}${separator}c=${encoded}`;
}

/**
 * Extracts and decodes consultation data from current URL
 * @returns Decoded user data if 'c' parameter exists, null otherwise
 */
export function getConsultationDataFromUrl(): ConsultationUserData | null {
  const params = new URLSearchParams(window.location.search);
  const encodedData = params.get('c');

  if (!encodedData) {
    return null;
  }

  try {
    return decodeUserData(encodedData);
  } catch (error) {
    console.error('[urlEncoding] Failed to get consultation data from URL:', error);
    return null;
  }
}

/**
 * Appends language parameter to EU form URL
 * Handles URLs with or without existing query parameters
 * @param euFormUrl Base EU form URL
 * @param language Language code (e.g., 'en', 'pl', 'cs')
 * @returns URL with language parameter appended
 */
export function appendLanguageToEuFormUrl(euFormUrl: string, language: string): string {
  try {
    const url = new URL(euFormUrl);
    // Set or update the language parameter
    url.searchParams.set('language', language);
    return url.toString();
  } catch (error) {
    // If URL parsing fails, fallback to simple string concatenation
    console.warn('[urlEncoding] Failed to parse URL, using fallback method:', error);
    const separator = euFormUrl.includes('?') ? '&' : '?';
    return `${euFormUrl}${separator}language=${encodeURIComponent(language)}`;
  }
}
